import { ChangeDetectionStrategy, Component, effect, forwardRef, input, inject } from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { Maybe } from '@stillmandigital/common';
import { Account } from '@api/core';
import { BusinessEntityEnum } from '@shared/enums/business-entity';

import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';

@Component({
  selector: 'app-business-entity-selector',
  imports: [FormsModule, ReactiveFormsModule, MatFormField, MatLabel, MatSelect, MatOption],
  templateUrl: './business-entity-selector.component.html',
  styleUrl: './business-entity-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BusinessEntitySelectorComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => BusinessEntitySelectorComponent),
      multi: true,
    },
  ],
})
export class BusinessEntitySelectorComponent implements ControlValueAccessor, Validator {
  private readonly _fb = inject(NonNullableFormBuilder);

  public readonly businessEntities = BusinessEntityEnum.getValuesByEnvType();

  public readonly businessEntity = this._fb.control<Maybe<Account.BusinessEntityEnum>>(undefined);

  public readonly required = input(false);

  public readonly readOnly = input(false);

  constructor() {
    effect(() => {
      if (this.required()) {
        this.businessEntity.addValidators(Validators.required);
      }
      if (this.readOnly()) {
        this.businessEntity.disable();
      }
    });
  }

  public writeValue(value: Maybe<Account.BusinessEntityEnum>): void {
    this.businessEntity.reset(value, { emitEvent: false });
  }

  public registerOnChange(fn: (value: Maybe<Account.BusinessEntityEnum>) => void): void {
    this.businessEntity.valueChanges.subscribe(fn);
  }

  public registerOnTouched(fn: () => void): void {
    this.businessEntity.valueChanges.subscribe(fn);
  }

  public validate(): ValidationErrors | null {
    return this.businessEntity.errors;
  }
}
